import React from "react";
import { Helmet } from "react-helmet-async";

export interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  image?: string;
  type?: "website" | "article" | "service";
  structuredData?: object;
  noindex?: boolean;
  nofollow?: boolean;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = "Brin's Handyman Services - Professional Home Repairs in Ottawa",
  description = "Your trusted source for professional handyman services in Ottawa and surrounding areas. We offer home repairs, painting, plumbing, carpentry, drywall, and furniture repairs.",
  canonical,
  image = "/logo512.png",
  type = "website",
  structuredData,
  noindex = false,
  nofollow = false,
}) => {
  const siteUrl =
    process.env.REACT_APP_SITE_URL || "https://brinshandymanservices.ca";
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const fullImage = image.startsWith("http") ? image : `${siteUrl}${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullCanonical} />

      {/* Robots */}
      <meta
        name="robots"
        content={
          noindex
            ? "noindex,nofollow"
            : nofollow
            ? "index,nofollow"
            : "index,follow"
        }
      />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content="Brin's Handyman Services" />
      <meta property="og:locale" content="en_CA" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Additional Meta Tags */}
      <meta name="author" content="Brin's Handyman Services" />
      <meta
        name="keywords"
        content="handyman services, home repairs, Ottawa, painting, plumbing, carpentry, drywall, furniture repairs, home maintenance"
      />
      <meta name="geo.region" content="CA-ON" />
      <meta name="geo.placename" content="Ottawa, Ontario" />
      <meta name="geo.position" content="45.4215;-75.6972" />
      <meta name="ICBM" content="45.4215, -75.6972" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
